import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, MessageSquare } from "lucide-react";

const looksLikeMobile = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (String(raw).trim().startsWith("+")) return digits.length >= 8 && digits.length <= 15;
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

/* Each person's own switch for job texts, plus the number we'd send them to. */
export const TextAlertsCard = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setState({
      sms_opt_in: data.sms_opt_in !== false,
      phone: data.phone || "",
      texting_enabled: data.texting_enabled,
    }));
  }, []);

  const save = async (patch) => {
    const next = { ...state, ...patch };
    if (next.phone && !looksLikeMobile(next.phone)) {
      toast.error("Enter a mobile number we can text, like (615) 555-0192");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/notifications",
        { sms_opt_in: next.sms_opt_in, phone: next.phone });
      setState({ ...next, ...data });
      toast.success(data.sms_opt_in ? "Text alerts are on" : "Text alerts are off");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  if (!state) return null;

  return (
    <section data-testid="text-alerts-card"
             className="overflow-hidden rounded-lg border border-border bg-white">
      <div className="flex items-start gap-3 border-b border-border px-5 py-4">
        <MessageSquare className="mt-0.5 h-4 w-4 text-gold" />
        <div>
          <h2 className="font-display text-base font-semibold text-royal-deep md:text-lg">
            Text alerts
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            A text when a job is assigned to you, when it moves, each morning, and for emergencies.
          </p>
        </div>
      </div>
      <div className="space-y-4 p-5">
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm text-royal-deep">Text me about my jobs</span>
          <Switch data-testid="sms-opt-in-switch" checked={state.sms_opt_in} disabled={busy}
                  onCheckedChange={(v) => save({ sms_opt_in: v })} />
        </label>
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Mobile number
          </Label>
          <div className="mt-1.5 flex gap-2">
            <Input data-testid="sms-phone-input" type="tel" inputMode="tel"
                   placeholder="(615) 555-0192" value={state.phone}
                   onChange={(e) => setState((s) => ({ ...s, phone: e.target.value }))}
                   className="h-11" />
            <Button type="button" data-testid="sms-phone-save" disabled={busy}
                    onClick={() => save({})}
                    className="h-11 bg-royal hover:bg-royal-deep">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </div>
        {!state.texting_enabled && (
          <p className="rounded-md border border-gold/40 bg-gold/5 px-3 py-2 text-xs text-royal-deep"
             data-testid="sms-not-configured">
            Texting isn't switched on for your company yet — add your Twilio details and these
            alerts start sending.
          </p>
        )}
      </div>
    </section>
  );
};
